import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { DEFAULT_SETTINGS, type UserSettings } from '@/types/domain';
import { ApiClient } from '@/services/api/apiClient';
import { useAuth } from '@/services/auth/authContext';
import { DataService } from './dataService';
import { AppDataContext } from './dataContext';

export function AppDataProvider({ children }: { children: ReactNode }) {
  const { getAccessToken } = useAuth();
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [online, setOnline] = useState(() => navigator.onLine);
  const [pendingWrites, setPendingWrites] = useState(0);

  const data = useMemo(() => new DataService(new ApiClient(getAccessToken)), [getAccessToken]);
  const dataRef = useRef(data);
  dataRef.current = data;

  const refreshPending = useCallback(async () => {
    setPendingWrites(await dataRef.current.pendingWrites());
  }, []);

  const syncNow = useCallback(async () => {
    await dataRef.current.flushOutbox();
    await refreshPending();
  }, [refreshPending]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const loaded = await data.getSettings();
      if (cancelled) return;
      setSettings(loaded);
      setSettingsLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [data]);

  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      void syncNow();
    };
    const goOffline = () => setOnline(false);

    globalThis.addEventListener('online', goOnline);
    globalThis.addEventListener('offline', goOffline);
    void refreshPending();

    return () => {
      globalThis.removeEventListener('online', goOnline);
      globalThis.removeEventListener('offline', goOffline);
    };
  }, [syncNow, refreshPending]);

  const updateSettings = useCallback(
    async (patch: Partial<UserSettings>) => {
      const next = { ...settings, ...patch };
      setSettings(next);
      await data.saveSettings(next);
      await refreshPending();
    },
    [data, settings, refreshPending],
  );

  useApplyAppearance(settings);

  const value = useMemo(
    () => ({ data, settings, settingsLoaded, updateSettings, online, pendingWrites, syncNow }),
    [data, settings, settingsLoaded, updateSettings, online, pendingWrites, syncNow],
  );

  return <AppDataContext.Provider value={value}>{children}</AppDataContext.Provider>;
}

/** Reflects appearance settings onto <html> where the CSS in index.css reads them. */
function useApplyAppearance(settings: UserSettings): void {
  useEffect(() => {
    const root = document.documentElement;
    const media = globalThis.matchMedia?.('(prefers-color-scheme: dark)');

    const applyTheme = () => {
      const dark = settings.theme === 'dark' || (settings.theme === 'system' && (media?.matches ?? true));
      root.classList.toggle('dark', dark);
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute('content', dark ? '#0b0b0f' : '#f6f6f8');
    };

    applyTheme();
    root.dataset.fontScale = settings.fontScale;
    root.dataset.contrast = settings.highContrast ? 'high' : 'normal';
    root.dataset.reduceMotion = String(settings.reduceMotion);

    if (settings.theme !== 'system' || !media) return;
    media.addEventListener('change', applyTheme);
    return () => media.removeEventListener('change', applyTheme);
  }, [settings.theme, settings.fontScale, settings.highContrast, settings.reduceMotion]);
}
